import type { Questionnaire } from '@medplum/fhirtypes'
import {
  getComputationAnswerOptions,
  getComputationQuestionnaireItem,
  QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION,
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/index.ts'
import {
  QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
  QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
} from '#src/structureDefinitions.ts'

const BRADEN_ANSWER_OPTIONS = {
  sensoryPerception: getComputationAnswerOptions([
    { code: 'completely-limited', score: 1 },
    { code: 'very-limited', score: 2 },
    { code: 'slightly-limited', score: 3 },
    { code: 'no-impairment', score: 4 },
  ]),
  moisture: getComputationAnswerOptions([
    { code: 'constantly-moist', score: 1 },
    { code: 'very-moist', score: 2 },
    { code: 'occasionally-moist', score: 3 },
    { code: 'rarely-moist', score: 4 },
  ]),
  activity: getComputationAnswerOptions([
    { code: 'bedfast', score: 1 },
    { code: 'chairfast', score: 2 },
    { code: 'walks-occasionally', score: 3 },
    { code: 'walks-frequently', score: 4 },
  ]),
  mobility: getComputationAnswerOptions([
    { code: 'completely-immobile', score: 1 },
    { code: 'very-limited', score: 2 },
    { code: 'slightly-limited', score: 3 },
    { code: 'no-limitations', score: 4 },
  ]),
  nutrition: getComputationAnswerOptions([
    { code: 'very-poor', score: 1 },
    { code: 'probably-inadequate', score: 2 },
    { code: 'adequate', score: 3 },
    { code: 'excellent', score: 4 },
  ]),
  frictionAndShear: getComputationAnswerOptions([
    { code: 'problem', score: 1 },
    { code: 'potential-problem', score: 2 },
    { code: 'no-apparent-problem', score: 3 },
  ]),
}

export const bradenQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/Braden',
  title: 'Braden Scale',
  name: 'Braden',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    tag: [VALIDATED_TOOL_TAG],
  },
  item: [
    {
      linkId: 'instructions',
      type: 'display',
      text: 'For predicting pressure sore risk',
    },
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'Braden PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString: 'https://www.in.gov/health/files/Braden_Scale.pdf',
        },
      ],
    },
    {
      linkId: 'questions',
      type: 'group',
      item: [
        {
          linkId: 'sensory-perception',
          type: 'choice',
          text: 'Sensory Perception - Ability to respond meaningfully to pressure-related discomfort',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.sensoryPerception,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Sensory Perception Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Completely limited: Unresponsive (does not moan, flinch, or grasp) to painful stimuli, due to diminished level of consciousness or sedation or limited ability to feel pain over most of body.\n\n2. Very limited: Responds only to painful stimuli. Cannot communicate discomfort except by moaning or restlessness.\n\n3. Slightly limited: Responds to verbal commands, but cannot always communicate discomfort or need to be turned, due to language barriers, cognitive impairment, or sedation.\n\n4. No impairment: Responds to verbal commands. Has no sensory deficit that would limit ability to feel or voice pain or discomfort.',
        },
        {
          linkId: 'moisture',
          type: 'choice',
          text: 'Moisture - Degree to which skin is exposed to moisture',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.moisture,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Moisture Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Constantly moist: Skin is kept moist almost constantly by perspiration, urine, etc.\n\n2. Often moist: Skin is often but not always moist. Linen must be changed at least once a shift.\n\n3. Occasionally moist: Skin is occasionally moist, requiring an extra linen change approximately once a day.\n\n4. Rarely moist: Skin is usually dry. Linen only requires changing at routine intervals.',
        },
        {
          linkId: 'activity',
          type: 'choice',
          text: 'Activity - Degree of physical activity',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.activity,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Activity Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Bedfast: Confined to bed.\n\n2. Chairfast: Ability to walk severely limited or non-existent. Cannot bear own weight and/or must be assisted into chair or wheelchair.\n\n3. Walks occasionally: Walks occasionally during the day, but for very short distances, with or without assistance.\n\n4. Walks frequently: Walks outside the room at least twice a day and inside the room at least once every 2 hours during waking hours.',
        },
        {
          linkId: 'mobility',
          type: 'choice',
          text: 'Mobility - Ability to change and control body position',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.mobility,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Mobility Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Completely immobile: Does not make even slight changes in body or extremity position without assistance.\n\n2. Very limited: Makes occasional slight changes in body or extremity position but unable to make frequent or significant changes independently.\n\n3. Slightly limited: Makes frequent though slight changes in body or extremity position independently.\n\n4. No limitation: Makes major and frequent changes in position without assistance.',
        },
        {
          linkId: 'nutrition',
          type: 'choice',
          text: 'Nutrition - Usual food intake pattern',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.nutrition,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Nutrition Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Very poor: Never eats a complete meal. Rarely eats more than one serving of protein (meat or dairy products) per day. Takes fluids poorly.\n\n2. Probably inadequate: Rarely eats a complete meal and generally eats only about half of any food offered. Protein intake includes only 3 servings of meat or dairy products per day. Occasionally will take a dietary supplement. Takes fluids poorly.\n\n3. Adequate: Eats over half of most meals. Eats a total of four servings of protein (meat or dairy products) each day. Occasionally will refuse a meal but will usually take a supplement if offered. Drinks 4-6 glasses of fluid per day.\n\n4. Excellent: Eats most of every meal. Never refuses a meal. Usually eats a total of four or more servings of meat and dairy products. Occasionally eats between meals. Does not require supplements. Drinks fluids frequently during the day.',
        },
        {
          linkId: 'friction-and-shear',
          type: 'choice',
          text: 'Friction and Shear',
          required: true,
          answerOption: BRADEN_ANSWER_OPTIONS.frictionAndShear,
          extension: [QUESTIONNAIRE_ITEM_DROPDOWN_EXTENSION],
        },
        {
          linkId: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
          type: 'display',
          prefix: 'Friction and Shear Description',
          extension: [
            {
              url: QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL,
              valueString: '{ "accordion": "true" }',
            },
          ],
          text: '1. Problem: Requires moderate to maximum assistance in moving. Complete lifting without sliding against sheets is impossible. Frequently slides down in bed or chair, requiring frequent repositioning with maximum assistance.\n\n2. Potential problem: Moves feebly or requires minimum assistance. During a move, skin probably slides to some extent against sheets, chair, restraints, or other devices. Maintains relatively good position in chair or bed most of the time but occasionally slides down.\n\n3. No apparent problem: Moves in bed and in chair independently and has sufficient muscle strength to lift up completely during move. Maintains good position in bed or chair.',
        },
      ],
    },
    getComputationQuestionnaireItem([
      { code: 'very-high-risk', score: 9 },
      { code: 'high-risk', score: 12 },
      { code: 'moderate-risk', score: 14 },
      { code: 'mild-risk', score: 18 },
      { code: 'no-risk', score: 23 },
    ]),
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire

export type BradenCategory =
  | 'Mild Risk'
  | 'Moderate Risk'
  | 'High Risk'
  | 'Severe Risk'

export function bradenCategory(score: number): BradenCategory | undefined {
  if (score >= 15) {
    return 'Mild Risk'
  }
  if (score >= 13 && score <= 14) {
    return 'Moderate Risk'
  }
  if (score >= 10 && score <= 12) {
    return 'High Risk'
  }
  if (score <= 9) {
    return 'Severe Risk'
  }
  return undefined
}
